import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui';
import api from '../services/api';

const CoachAcknowledgement = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [acknowledged, setAcknowledged] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== 'coach') {
      navigate('/dashboard');
      return;
    }

    if (user?.coachAcknowledgement?.acknowledged) {
      navigate('/dashboard');
    }
  }, [navigate, user]);

  const handleScroll = (event) => {
    const atBottom = event.target.scrollHeight - event.target.scrollTop <= event.target.clientHeight + 40;
    if (atBottom) {
      setHasScrolled(true);
    }
  };

  const handleSubmit = async () => {
    if (!hasScrolled) {
      setError('Please scroll to the bottom of the document before continuing.');
      return;
    }

    if (!acknowledged) {
      setError('Please check the acknowledgement box to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/auth/acknowledge-guidelines', { documentVersion: '1.0' });
      await refreshUser();
      navigate('/dashboard');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to save acknowledgement.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 px-4 py-8">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-soft-lg border border-neutral-200 bg-white shadow-soft-lg">
        <div className="border-b border-neutral-200 bg-primary-600 px-6 py-5 text-white">
          <h1 className="text-2xl font-display font-semibold">Coach Engagement Guidelines</h1>
          <p className="mt-1 text-sm text-primary-100">Required acknowledgment before accessing the coach dashboard</p>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-5 text-sm text-neutral-700" onScroll={handleScroll}>
          <p className="mb-4 rounded-soft border border-warning-200 bg-warning-50 p-3 text-warning-800">
            Read this full document and scroll to the bottom to enable acknowledgment.
          </p>

          <h2 className="mb-2 text-base font-semibold text-neutral-900">Scope of Role</h2>
          <p className="mb-4">As a coach, you support participant consistency, engagement, and goal completion. You do not provide clinical diagnosis, crisis intervention, or medical advice within this platform.</p>

          <h2 className="mb-2 text-base font-semibold text-neutral-900">Data Boundaries</h2>
          <ul className="mb-4 list-disc space-y-1 pl-5">
            <li>Access is limited to assigned participants with active engagement consent.</li>
            <li>View only engagement trends and goal status available in the coach dashboard.</li>
            <li>Do not attempt to access clinical records or private journal content.</li>
          </ul>

          <h2 className="mb-2 text-base font-semibold text-neutral-900">Messaging Rules</h2>
          <ul className="mb-4 list-disc space-y-1 pl-5">
            <li>Use approved encouragement templates only.</li>
            <li>One message per participant per week maximum.</li>
            <li>Messages should reinforce engagement and goal adherence only.</li>
          </ul>

          <h2 className="mb-2 text-base font-semibold text-neutral-900">Safety and Escalation</h2>
          <ul className="mb-4 list-disc space-y-1 pl-5">
            <li>The platform is not a crisis response channel.</li>
            <li>If a safety concern appears, follow your escalation protocol and emergency referral steps.</li>
            <li>Do not use encouragement messaging for urgent intervention.</li>
          </ul>

          <h2 className="mb-2 text-base font-semibold text-neutral-900">Compliance</h2>
          <ul className="mb-4 list-disc space-y-1 pl-5">
            <li>Maintain participant confidentiality and secure account access.</li>
            <li>Only access participant records for legitimate care-coordination purposes.</li>
            <li>Violations may result in account suspension.</li>
          </ul>

          <p className="mt-6 text-xs text-neutral-500">Version 1.0 • February 2026</p>
        </div>

        <div className="border-t border-neutral-200 bg-neutral-50 px-6 py-5">
          {error && <div className="mb-3 rounded-soft border border-error-200 bg-error-50 p-3 text-sm text-error-700">{error}</div>}

          <label className="mb-4 flex items-start gap-3 text-sm text-neutral-700">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>I acknowledge and agree to follow the Coach Engagement Guidelines.</span>
          </label>

          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={() => { logout(); navigate('/login'); }}>
              Decline and Logout
            </Button>
            <Button onClick={handleSubmit} disabled={loading || !hasScrolled || !acknowledged}>
              {loading ? 'Submitting...' : 'Acknowledge and Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachAcknowledgement;
